import { AbstractModel } from '../base';
import {
  Department,
  DepartmentFieldError,
  IDepartment,
  IDepartmentFieldError
} from '../department';
import { State } from '../state/state';
import { IUser, IUserData, User } from '../user';

import {
  IWorkflowFormField,
  IWorkflowFormFieldData,
  IWorkflowFormFieldOptionData,
  IWorkflowFormTemplateRevision,
  IWorkflowFormTemplateRevisionData,
  IWorkflowFormSectionData,
  IWorkflowFormSection,
  IWorkflowFormSectionField,
  IWorkflowFormSectionFieldData,
  IWorkflowFormFieldOption,
  IWorkflowFormTemplateRevisionFieldError,
  IWorkflowFormSectionFieldErrorData,
  IWorkflowFormTemplateRevisionFieldErrorData,
  IWorkflowFormSectionFieldError,
  IWorkflowFormSectionFieldFieldErrorData,
  IWorkflowFormSectionFieldFieldError,
  IWorkflowFormFieldFieldErrorData,
  IWorkflowFormFieldFieldError,
  IWorkflowFormFieldOptionTodo,
  IWorkflowFormFieldOptionTodoData,
  IWorkflowFormFieldOptionTodoTemplateData,
  IWorkflowFormFieldOptionTodoTemplate,
  IWorkflowFormSectionFieldConstraint,
  IWorkflowFormSectionFieldConstraintConditionData,
  IWorkflowFormSectionFieldConstraintCondition,
  IWorkflowFormTemplateRevisionStatusData,
  IWorkflowFormTemplateRevisionStatus,
  IWorkflowFormSectionFieldConstraintData,
  IWorkflowFormFieldType,
  IWorkflowFormFieldOptionFieldError,
  IWorkflowFormFieldStyleFieldError,
  IWorkflowFormFieldNode,
  IWorkflowFormFieldNodeData,
  IWorkflowFormTemplateConfigHeader,
  IWorkflowFormTemplateConfigHeaderData,
  IWorkflowFormTemplateRevisionConfig,
  IWorkflowFormTemplateRevisionConfigData,
  IWorkflowFormTemplate,
  IWorkflowFormTemplateData,
  IWorkflowFormFieldConstraintConditionType,
  IWorkflowFormFieldConstraintComparisonType,
  IAccessPredicateNode,
  IAccessPredicateNodeData,
  IAccessPredicateNodeOperatorInfo,
  IAccessPredicateNodeOperandTypeInfo,
  IWorkflowFormTemplateRevisionAccessCondition,
  IWorkflowFormTemplateRevisionAccessConditionData,
  IAccessConditionTarget,
  IAccessConditionTargetData
} from './workflow-form-template.types';
import {
  WorkflowFormFieldTypeEnum,
  WorkflowConstraintActionEnum,
  WorkflowFormTemplateStateEnum,
  WorkflowFormTemplatePrefixTypeEnum,
  WorkflowFormTemplateConfigHeaderTypeEnum,
  WorkflowConstraintConditionTypeEnum,
  WorkflowConstraintConditionComparisonOperators,
  AccessPredicateNodeBinaryOperators,
  AccessPredicateNodeOperandSubtype,
  AccessTargetType
} from './workflow-form-template.constants';
import { Group } from '@vue-altoleap-libraries/vue-altoleap-accounts-lib';
import { padStart } from 'lodash';
import { AbstractComputationNode } from '../computation/computation';
import {
  ComputationBinaryOperators,
  ComputationNodeType,
  ExpressionNodeTree,
  convertExpressionTreeToNodeList,
  convertNodeListToExpressionTree
} from '../computation';
import {
  AbstractPredicateNode,
  AbstractPredicateNodeOperatorInfo
} from '../predicate/predicate';
import {
  ExpressionPredicateNodeTree,
  convertPredicateNodeListToLogicalExpressionTree
} from '../predicate/tree';
import { IWorkflowFormFieldSubmission } from '../workflow-form/workflow-form.types';
import {
  AbstractAttachment,
  IAttachment,
  IAttachmentData
} from '../attachment';

export class WorkflowFormTemplateAttachment extends AbstractAttachment {
  constructor(data: IAttachmentData) {
    super(data);
  }

  toString(): string {
    return this.filename;
  }
}
export class WorkflowFormTemplateConfigHeader extends AbstractModel
  implements IWorkflowFormTemplateConfigHeader {
  readonly id?: number;
  readonly meta_label: string = '';
  readonly display_label: string = '';
  readonly order: number = 1;
  readonly width: number;
  readonly type: WorkflowFormTemplateConfigHeaderTypeEnum =
    WorkflowFormTemplateConfigHeaderTypeEnum.Table;

  constructor(data: IWorkflowFormTemplateConfigHeaderData) {
    super();

    this.validate(data, ['width']);

    if (data.id) {
      this.id = data.id;
    }

    this.width = data.width!;

    if (data.meta_label) {
      this.meta_label = data.meta_label;
    }

    if (data.display_label) {
      this.display_label = data.display_label;
    }

    if (data.order) {
      this.order = data.order;
    }

    if (data.type) {
      this.type = data.type;
    }
  }
}

export class WorkflowFormTemplateConfig extends AbstractModel
  implements IWorkflowFormTemplateRevisionConfig {
  readonly id?: number;
  readonly headers: IWorkflowFormTemplateConfigHeader[] = [];

  constructor(data?: IWorkflowFormTemplateRevisionConfigData) {
    super();

    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      if (data.headers) {
        this.headers = data.headers.map(
          header => new WorkflowFormTemplateConfigHeader(header)
        );
      }
    }
  }
}

export class WorkflowFormTemplate extends AbstractModel
  implements IWorkflowFormTemplate {
  readonly id?: number;
  readonly title: string;
  readonly description: string;
  readonly series_origin: number = 1;
  readonly prefix: string;
  readonly prefix_type: WorkflowFormTemplatePrefixTypeEnum =
    WorkflowFormTemplatePrefixTypeEnum.Text;
  readonly config: IWorkflowFormTemplateRevisionConfig = new WorkflowFormTemplateConfig();
  readonly attachments: IAttachment[] = [];

  readonly stakeholder_groups: (number | Group)[] = [];
  readonly stakeholder_users: (number | IUser)[] = [];

  constructor(data: IWorkflowFormTemplateData) {
    super();

    this.validate(data, ['title']);
    if (data.id) {
      this.id = data.id;
    }

    this.title = data.title!;

    this.description = data.description ?? '';

    this.series_origin = data.series_origin ?? 1;

    this.prefix = data.prefix ?? '';

    if (data.prefix_type) {
      this.prefix_type = data.prefix_type;
    }

    if (data.config) {
      this.config = new WorkflowFormTemplateConfig(data.config);
    }

    if (data.attachments) {
      this.attachments = data.attachments.map(
        attachment => new WorkflowFormTemplateAttachment(attachment)
      );
    }

    if (data.stakeholder_groups) {
      this.stakeholder_groups = data.stakeholder_groups.map(group =>
        typeof group === 'number' ? group : new Group(group)
      );
    }

    if (data.stakeholder_users) {
      this.stakeholder_users = data.stakeholder_users.map(group =>
        typeof group === 'number' ? group : new User(group)
      );
    }
  }

  get seriesOriginId() {
    if (this.prefix) {
      return `${this.prefix}-${padStart(String(this.series_origin), 4, '0')}`;
    }

    return this.series_origin.toString();
  }

  toString() {
    return this.title;
  }
}

export class WorkflowFormTemplateRevision extends AbstractModel
  implements IWorkflowFormTemplateRevision {
  readonly id?: number;
  readonly state: IWorkflowFormTemplateRevisionStatus = new WorkflowFormTemplateStatus();
  readonly owner?: string;
  readonly sections: IWorkflowFormSection[] = [];
  readonly workflow_groups: Group[] = [];
  readonly access_conditions: IWorkflowFormTemplateRevisionAccessCondition[] = [];

  readonly template?: IWorkflowFormTemplate;
  readonly workflow: number;
  readonly revision: number;
  readonly created_at?: Date;
  readonly updated_at?: Date;

  constructor(data: IWorkflowFormTemplateRevisionData) {
    super();

    this.validate(data, ['workflow']);

    this.workflow = data.workflow!;

    if (data.id) {
      this.id = data.id;
    }

    if (data.template) {
      this.template = new WorkflowFormTemplate(data.template);
    }

    if (data.state) {
      this.state =
        typeof data.state === 'number'
          ? new WorkflowFormTemplateStatus({
              value: data.state
            })
          : data.state;
    }

    if (data.workflow_groups) {
      this.workflow_groups = data.workflow_groups.map(
        group => new Group(group)
      );
    }

    if (data.access_conditions) {
      this.access_conditions = data.access_conditions.map(
        condition => new WorkflowFormTemplateRevisionAccessCondition(condition)
      );
    }

    if (data.sections) {
      this.sections = data.sections.map(data => new WorkflowFormSection(data));
    }

    this.owner = data.owner ?? '';

    this.revision = data.revision!;

    if (data.created_at) {
      this.created_at =
        typeof data.created_at == 'string'
          ? new Date(data.created_at)
          : data.created_at;
    }

    if (data.updated_at) {
      this.updated_at =
        typeof data.updated_at == 'string'
          ? new Date(data.updated_at)
          : data.updated_at;
    }
  }

  toString() {
    return `${this.template?.title}R${this.revision}`;
  }

  isPublished(): boolean {
    return this.state.valueOf() == WorkflowFormTemplateStateEnum.Published;
  }
}

class WorkflowFormTemplateState extends State<WorkflowFormTemplateStateEnum> {
  static readonly Draft = new State({
    value: WorkflowFormTemplateStateEnum.Draft,
    display_name:
      WorkflowFormTemplateStateEnum[WorkflowFormTemplateStateEnum.Draft],
    message: 'Draft',
    icon: 'mdi-file-document-outline',
    color: 'draft'
  });

  public static readonly Published = new State({
    value: WorkflowFormTemplateStateEnum.Published,
    display_name:
      WorkflowFormTemplateStateEnum[WorkflowFormTemplateStateEnum.Published],
    message: 'Published',
    icon: 'mdi-file-send-outline',
    color: 'success'
  });

  public static readonly Archived = new State({
    value: WorkflowFormTemplateStateEnum.Archived,
    display_name:
      WorkflowFormTemplateStateEnum[WorkflowFormTemplateStateEnum.Archived],
    message: 'Archived',
    icon: 'mdi-close',
    color: 'cancel'
  });

  public static readonly Default = new State({
    value: -1,
    display_name: '',
    color: 'white'
  });
}

export class WorkflowFormTemplateStatus
  implements IWorkflowFormTemplateRevisionStatusData {
  readonly value: number = WorkflowFormTemplateStateEnum.Draft;
  readonly display_name?: string = 'Draft';

  constructor(data?: IWorkflowFormTemplateRevisionStatusData) {
    if (data) {
      this.value = data.value;
      if (data.display_name) {
        this.display_name = data.display_name;
      }
    }
  }

  getCurrentState(): State<WorkflowFormTemplateStateEnum> {
    let state = null;
    switch (this.value) {
      case WorkflowFormTemplateStateEnum.Draft:
        state = WorkflowFormTemplateState.Draft;
        break;

      case WorkflowFormTemplateStateEnum.Published:
        state = WorkflowFormTemplateState.Published;
        break;

      default:
        state = WorkflowFormTemplateState.Default;
    }
    return state;
  }

  valueOf() {
    return this.value;
  }
}

export class WorkflowFormSection implements IWorkflowFormSection {
  readonly id?: number;
  readonly title: string;
  readonly order: number;
  readonly section_fields: IWorkflowFormSectionField[];

  constructor(data: IWorkflowFormSectionData) {
    if (data.id) {
      this.id = data.id;
    }

    this.title = data.title ?? '';
    this.order = data.order;

    this.section_fields = data.section_fields.map(
      data => new WorkflowFormSectionField(data)
    );
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class WorkflowFormSectionField implements IWorkflowFormSectionField {
  readonly id?: number;
  readonly order: number;
  readonly width: number;
  readonly field_template: IWorkflowFormField | number;
  readonly required: boolean;
  readonly disabled: boolean;
  readonly hidden: boolean = false;
  readonly create_todo: boolean;
  readonly constraints: IWorkflowFormSectionFieldConstraint[] = [];

  constructor(data: IWorkflowFormSectionFieldData) {
    if (data.id) {
      this.id = data.id;
    }
    this.order = data.order;
    this.width = data.width;
    this.required = data.required ?? false;
    this.disabled = data.disabled ?? false;

    if (data.hidden) {
      this.hidden = data.hidden;
    }

    if (typeof data.field_template == 'number') {
      this.field_template = data.field_template;
    } else {
      this.field_template = new WorkflowFormField(data.field_template);
    }
    this.create_todo = data.create_todo ?? false;
    this.constraints = data.constraints.map(
      constraint => new WorkflowFormSectionFieldConstraint(constraint)
    );
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class WorkflowFormSectionFieldConstraint
  implements IWorkflowFormSectionFieldConstraint {
  readonly id?: number;
  readonly action: WorkflowConstraintActionEnum;
  readonly conditions: IWorkflowFormSectionFieldConstraintCondition[] = [];

  constructor(data: IWorkflowFormSectionFieldConstraintData) {
    if (data.id) {
      this.id = data.id;
    }

    this.action = data.action;

    this.conditions = data.conditions.map(
      condition => new WorkflowFormSectionFieldConstraintCondition(condition)
    );
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }

  isSatisfied(
    answers: IWorkflowFormFieldSubmission[],
    templateIndex?: number
  ): boolean {
    return this.conditions.every(condition =>
      condition.isMet(answers, templateIndex)
    );
  }
}

export class WorkflowFormFieldConstraintConditionType
  implements IWorkflowFormFieldConstraintConditionType {
  readonly value: WorkflowConstraintConditionTypeEnum;
  readonly display_name: string;
  readonly icon: string;

  constructor(data: IWorkflowFormFieldConstraintConditionType) {
    this.value = data.value;
    this.display_name = data.display_name;
    this.icon = data.icon;
  }

  static OptionBased = new WorkflowFormFieldConstraintConditionType({
    icon: 'mdi-checkbox-marked-outline',
    display_name: 'Option',
    value: WorkflowConstraintConditionTypeEnum.OptionBased
  });

  static ValueBased = new WorkflowFormFieldConstraintConditionType({
    icon: 'mdi-format-quote-close',
    display_name: 'Value',
    value: WorkflowConstraintConditionTypeEnum.ValueBased
  });

  public static getWorkflowFormFieldConstraintConditionTypeList(): IWorkflowFormFieldConstraintConditionType[] {
    return [
      WorkflowFormFieldConstraintConditionType.OptionBased,
      WorkflowFormFieldConstraintConditionType.ValueBased
    ];
  }
}

export class WorkflowFormFieldConstraintComparisonOperators
  implements IWorkflowFormFieldConstraintComparisonType {
  readonly value: WorkflowConstraintConditionComparisonOperators;
  readonly display_name: string;
  readonly icon: string;

  constructor(data: IWorkflowFormFieldConstraintComparisonType) {
    this.value = data.value;
    this.display_name = data.display_name;
    this.icon = data.icon;
  }

  toString(): string {
    return this.display_name;
  }

  static Equals = new WorkflowFormFieldConstraintComparisonOperators({
    display_name: 'Equals',
    value: WorkflowConstraintConditionComparisonOperators.Equals,
    icon: 'mdi-equal'
  });

  static NotEquals = new WorkflowFormFieldConstraintComparisonOperators({
    display_name: 'Not equals',
    value: WorkflowConstraintConditionComparisonOperators.NotEquals,
    icon: 'mdi-not-equal'
  });

  static GreaterThan = new WorkflowFormFieldConstraintComparisonOperators({
    display_name: 'Greater than',
    value: WorkflowConstraintConditionComparisonOperators.GreaterThan,
    icon: 'mdi-greater-than'
  });

  static GreaterThanOrEqual = new WorkflowFormFieldConstraintComparisonOperators(
    {
      display_name: 'Greater than or equals',
      value: WorkflowConstraintConditionComparisonOperators.GreaterThanOrEqual,
      icon: 'mdi-greater-than-or-equal'
    }
  );

  static LessThan = new WorkflowFormFieldConstraintComparisonOperators({
    display_name: 'Less than',
    value: WorkflowConstraintConditionComparisonOperators.LessThan,
    icon: 'mdi-less-than'
  });

  static LessThanOrEqual = new WorkflowFormFieldConstraintComparisonOperators({
    display_name: 'Less than or equals',
    value: WorkflowConstraintConditionComparisonOperators.LessThanOrEqual,
    icon: 'mdi-less-than-or-equal'
  });

  static Contains = new WorkflowFormFieldConstraintComparisonOperators({
    display_name: 'Contains',
    value: WorkflowConstraintConditionComparisonOperators.Contains,
    icon: 'mdi-arrow-right-bold-box'
  });

  public static getWorkflowFormFieldConstraintComparisonTypeList(): IWorkflowFormFieldConstraintComparisonType[] {
    return [
      WorkflowFormFieldConstraintComparisonOperators.Equals,
      WorkflowFormFieldConstraintComparisonOperators.NotEquals,
      WorkflowFormFieldConstraintComparisonOperators.GreaterThan,
      WorkflowFormFieldConstraintComparisonOperators.GreaterThanOrEqual,
      WorkflowFormFieldConstraintComparisonOperators.LessThan,
      WorkflowFormFieldConstraintComparisonOperators.LessThanOrEqual,
      WorkflowFormFieldConstraintComparisonOperators.Contains
    ];
  }

  public static getWorkflowFormFieldConstraintComparisonTypeByType(
    type: WorkflowConstraintConditionComparisonOperators
  ): IWorkflowFormFieldConstraintComparisonType | null {
    return (
      WorkflowFormFieldConstraintComparisonOperators.getWorkflowFormFieldConstraintComparisonTypeList().find(
        item => item.value === type
      ) ?? null
    );
  }
}

export class WorkflowFormSectionFieldConstraintCondition
  implements IWorkflowFormSectionFieldConstraintCondition {
  readonly id?: number;
  readonly field_option: number | null = null;
  readonly form_section_field: number;
  readonly condition_type: WorkflowConstraintConditionTypeEnum;
  readonly comparison_operator: WorkflowConstraintConditionComparisonOperators | null;
  readonly comparison_value: string = '';

  constructor(data: IWorkflowFormSectionFieldConstraintConditionData) {
    if (data.id) {
      this.id = data.id;
    }

    if (data.field_option) {
      this.field_option = data.field_option;
    }

    this.form_section_field = data.form_section_field;

    this.condition_type =
      data.condition_type ?? WorkflowConstraintConditionTypeEnum.OptionBased;

    this.comparison_operator = data.comparison_operator ?? null;

    if (data.comparison_value) {
      this.comparison_value = data.comparison_value;
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }

  isMet(
    answers: IWorkflowFormFieldSubmission[],
    templateIndex?: number
  ): boolean {
    const answer = answers.find(answer => {
      let found = false;
      if (typeof answer.field == 'number') {
        found = answer.field == this.form_section_field;
      } else {
        found =
          (answer.field as IWorkflowFormSectionFieldData).id ==
          this.form_section_field;
      }
      if (found && templateIndex !== undefined) {
        found = answer.template_index === templateIndex;
      }
      return found;
    });

    if (answer) {
      // Branch by condition type: OptionBased uses selected options;
      // ValueBased compares the field's value using comparison_operator and comparison_value
      // Option-Based comparison
      if (
        this.condition_type === WorkflowConstraintConditionTypeEnum.OptionBased
      ) {
        return (
          answer.option_submissions.filter(option => {
            if (typeof option == 'number') {
              return option === this.field_option;
            }
            return option.option === this.field_option;
          }).length > 0
        );
      }

      // Value-Based comparison
      const leftRaw = (answer.value ?? '').toString();
      const rightRaw = (this.comparison_value ?? '').toString();
      const operator =
        this.comparison_operator ??
        WorkflowConstraintConditionComparisonOperators.Equals;

      // Try numeric comparison when both sides are numeric
      const leftNum = parseFloat(leftRaw);
      const rightNum = parseFloat(rightRaw);
      const bothNumeric = !Number.isNaN(leftNum) && !Number.isNaN(rightNum);

      switch (operator) {
        case WorkflowConstraintConditionComparisonOperators.Equals:
          return bothNumeric ? leftNum === rightNum : leftRaw === rightRaw;
        case WorkflowConstraintConditionComparisonOperators.NotEquals:
          return bothNumeric ? leftNum !== rightNum : leftRaw !== rightRaw;
        case WorkflowConstraintConditionComparisonOperators.GreaterThan:
          return bothNumeric
            ? leftNum > rightNum
            : leftRaw.localeCompare(rightRaw) > 0;
        case WorkflowConstraintConditionComparisonOperators.GreaterThanOrEqual:
          return bothNumeric
            ? leftNum >= rightNum
            : leftRaw.localeCompare(rightRaw) >= 0;
        case WorkflowConstraintConditionComparisonOperators.LessThan:
          return bothNumeric
            ? leftNum < rightNum
            : leftRaw.localeCompare(rightRaw) < 0;
        case WorkflowConstraintConditionComparisonOperators.LessThanOrEqual:
          return bothNumeric
            ? leftNum <= rightNum
            : leftRaw.localeCompare(rightRaw) <= 0;
        case WorkflowConstraintConditionComparisonOperators.Contains:
          return leftRaw.includes(rightRaw);
        default:
          return false;
      }
    } else {
      return false;
    }
  }
}

export class WorkflowFormFieldType implements IWorkflowFormFieldType {
  readonly value: WorkflowFormFieldTypeEnum;
  readonly text: string;
  readonly icon: string;
  readonly disabled: boolean;

  constructor(data: IWorkflowFormFieldType) {
    this.value = data.value;
    this.text = data.text;
    this.icon = data.icon;
    this.disabled = data.disabled ?? false;
  }

  public toString(): string {
    return this.text;
  }

  static Label = new WorkflowFormFieldType({
    icon: 'mdi-format-text',
    text: 'Label',
    value: WorkflowFormFieldTypeEnum.Label
  });

  static Textfield = new WorkflowFormFieldType({
    icon: 'mdi-form-textbox',
    text: 'Short input box',
    value: WorkflowFormFieldTypeEnum.Textfield
  });

  static Textarea = new WorkflowFormFieldType({
    icon: 'mdi-text-recognition',
    text: 'Long input box',
    value: WorkflowFormFieldTypeEnum.Textarea
  });

  static Combobox = new WorkflowFormFieldType({
    icon: 'mdi-form-dropdown',
    text: 'Dropdown',
    value: WorkflowFormFieldTypeEnum.Combobox
  });

  static DatePicker = new WorkflowFormFieldType({
    icon: 'mdi-calendar-range',
    text: 'Date',
    value: WorkflowFormFieldTypeEnum.DatePicker
  });

  static Checkbox = new WorkflowFormFieldType({
    icon: 'mdi-order-bool-ascending-variant',
    text: 'Checklist',
    value: WorkflowFormFieldTypeEnum.Checkbox
  });

  static Radiobutton = new WorkflowFormFieldType({
    icon: 'mdi-radiobox-marked',
    text: 'Radio Button',
    value: WorkflowFormFieldTypeEnum.Radiobutton
  });

  static FileField = new WorkflowFormFieldType({
    icon: 'mdi-paperclip',
    text: 'Single-File',
    value: WorkflowFormFieldTypeEnum.FileField,
    disabled: true
  });

  static MultiFileField = new WorkflowFormFieldType({
    icon: 'mdi-paperclip',
    text: 'Multi-File',
    value: WorkflowFormFieldTypeEnum.MultiFileField
  });

  static NumericField = new WorkflowFormFieldType({
    icon: 'mdi-numeric',
    text: 'Numeric Field',
    value: WorkflowFormFieldTypeEnum.NumericField
  });

  static Spacer = new WorkflowFormFieldType({
    icon: 'mdi-arrow-expand-horizontal',
    text: 'Spacer',
    value: WorkflowFormFieldTypeEnum.Spacer
  });

  static CalculationField = new WorkflowFormFieldType({
    icon: 'mdi-abacus',
    text: 'Calculation',
    value: WorkflowFormFieldTypeEnum.CalculationField
  });

  static TemplateField = new WorkflowFormFieldType({
    icon: 'mdi-table-edit',
    text: 'Template',
    value: WorkflowFormFieldTypeEnum.TemplateField
  });

  static getWorkflowFormFieldTypeList(): IWorkflowFormFieldType[] {
    return [
      WorkflowFormFieldType.Label,
      WorkflowFormFieldType.Textfield,
      WorkflowFormFieldType.Textarea,
      WorkflowFormFieldType.NumericField,
      WorkflowFormFieldType.DatePicker,
      WorkflowFormFieldType.Combobox,
      WorkflowFormFieldType.Checkbox,
      WorkflowFormFieldType.Radiobutton,
      WorkflowFormFieldType.MultiFileField,
      WorkflowFormFieldType.FileField,
      WorkflowFormFieldType.CalculationField,
      WorkflowFormFieldType.TemplateField,
      WorkflowFormFieldType.Spacer
    ];
  }
}

export class WorkflowFormFieldNode extends AbstractComputationNode
  implements IWorkflowFormFieldNode {
  readonly id?: number;
  readonly variable_name: string = '';

  constructor(data: IWorkflowFormFieldNodeData) {
    super(data);

    if (data.id) {
      this.id = data.id;
    }

    if (data.variable_name) {
      this.variable_name = data.variable_name;
    }
  }

  public isOperand(): boolean {
    return this.type == ComputationNodeType.Operand;
  }

  public isOperator(): boolean {
    return this.type == ComputationNodeType.Operator;
  }

  public isUnaryOperator(): boolean {
    if (!this.isOperator()) {
      return false;
    }
    switch (this.subtype) {
      case ComputationBinaryOperators.Ceil:
      case ComputationBinaryOperators.Floor:
      case ComputationBinaryOperators.Abs:
        return true;
      default:
        return false;
    }
  }

  public isStartBracket(): boolean {
    return Boolean(
      this.type === ComputationNodeType.Operator &&
        this.subtype === ComputationBinaryOperators.Start_bracket
    );
  }

  public isCloseBracket(): boolean {
    return Boolean(
      this.type === ComputationNodeType.Operator &&
        this.subtype === ComputationBinaryOperators.Close_bracket
    );
  }
}

export class WorkflowFormField implements IWorkflowFormField {
  readonly id?: number;
  readonly label: string;
  readonly meta_label: string;
  readonly hint: string;
  readonly type: WorkflowFormFieldTypeEnum;
  readonly options: WorkflowFormFieldOption[] = [];
  readonly nodes: IWorkflowFormFieldNode[] = [];
  readonly urgency_threshold: number = 0;
  readonly template: IWorkflowFormField | number | null = null;

  constructor(data: IWorkflowFormFieldData) {
    if (data.id) {
      this.id = data.id;
    }
    this.hint = data.hint ?? '';
    this.label = data.label ?? '';
    this.meta_label = data.meta_label ?? '';
    this.type = data.type;

    if (data.options) {
      this.options = data.options.map(
        data => new WorkflowFormFieldOption(data)
      );
    }
    if (data.nodes) {
      this.nodes = data.nodes.map(data => new WorkflowFormFieldNode(data));
    }

    if (data.urgency_threshold) {
      this.urgency_threshold = data.urgency_threshold;
    }

    if (data.template) {
      this.template =
        typeof data.template == 'number'
          ? data.template
          : new WorkflowFormField(data.template);
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }

  toNodeList(
    tree: ExpressionNodeTree<IWorkflowFormFieldNode>
  ): IWorkflowFormFieldNode[] {
    return convertExpressionTreeToNodeList(tree);
  }

  toExpressionTree(): ExpressionNodeTree<IWorkflowFormFieldNode> {
    return convertNodeListToExpressionTree(this.nodes);
  }

  public static MAX_EXPRESSION_TREE_DEPTH = 2;
}

export class WorkflowFormFieldOption implements IWorkflowFormFieldOption {
  readonly id?: number;
  readonly value: string;
  readonly order: number;
  readonly field: number;

  constructor(data: IWorkflowFormFieldOptionData) {
    if (data.id) {
      this.id = data.id;
    }

    this.value = data.value;
    this.field = data.field;
    this.order = data.order;
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class WorkflowFormFieldOptionTodo
  implements IWorkflowFormFieldOptionTodo {
  readonly id?: number;
  readonly field_option: number;
  readonly todo_template: IWorkflowFormFieldOptionTodoTemplate | number;
  readonly section_field: number;

  constructor(data: IWorkflowFormFieldOptionTodoData) {
    if (data.id) {
      this.id = data.id;
    }

    this.field_option = data.field_option;

    if (typeof data.todo_template == 'number') {
      this.todo_template = data.todo_template;
    } else {
      this.todo_template = new WorkflowFormFieldOptionTodoTemplate(
        data.todo_template
      );
    }

    this.section_field = data.section_field;
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class WorkflowFormFieldOptionTodoTemplate
  implements IWorkflowFormFieldOptionTodoTemplate {
  readonly id?: number;
  readonly todo_text: string;
  readonly todo_department: IDepartment | number;
  readonly assignee: IUserData | number;
  readonly order: number;

  constructor(data: IWorkflowFormFieldOptionTodoTemplateData) {
    if (data.id) {
      this.id = data.id;
    }

    this.todo_text = data.todo_text ?? '';

    if (typeof data.todo_department == 'number') {
      this.todo_department = data.todo_department;
    } else {
      this.todo_department = new Department(data.todo_department);
    }

    if (typeof data.assignee == 'number') {
      this.assignee = data.assignee;
    } else {
      this.assignee = new User(data.assignee);
    }

    this.order = data.order;
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class WorkflowFormTemplateFieldError
  implements IWorkflowFormTemplateRevisionFieldError {
  readonly title: string[] = [];
  readonly description: string[] = [];
  readonly sections: IWorkflowFormSectionFieldError[] = [];
  readonly owner: string[] = [];

  constructor(data?: IWorkflowFormTemplateRevisionFieldErrorData) {
    if (data) {
      if (data.title) {
        this.title = data.title;
      }
      if (data.description) {
        this.description = data.description;
      }
      if (data.owner) {
        this.owner = data.owner;
      }
      if (data.sections) {
        this.sections = data.sections.map(
          section => new WorkflowFormSectionFieldError(section)
        );
      }
    }
  }

  clearErrors(): void {
    this.title.splice(0, this.title.length);
    this.description.splice(0, this.description.length);
    this.sections.splice(0, this.sections.length);
  }

  static isWorkflowFormTemplateFieldErrorData(
    data: IWorkflowFormTemplateRevisionFieldErrorData
  ): boolean {
    if (data.title && Array.isArray(data.title)) {
      return true;
    } else if (data.description && Array.isArray(data.description)) {
      return true;
    } else if (data.sections && Array.isArray(data.sections)) {
      return true;
    }
    return false;
  }
}

export class WorkflowFormSectionFieldError
  implements IWorkflowFormSectionFieldError {
  readonly order: string[] = [];
  readonly title: string[] = [];
  readonly section_fields: IWorkflowFormSectionFieldFieldError[] = [];

  constructor(data?: IWorkflowFormSectionFieldErrorData) {
    if (data) {
      if (data.order) {
        this.order = data.order;
      }
      if (data.section_fields) {
        this.section_fields = data.section_fields.map(
          section => new WorkflowFormSectionFieldFieldError(section)
        );
      }
    }
  }

  clearErrors(): void {
    this.title.splice(0, this.title.length);
    this.order.splice(0, this.order.length);
    this.section_fields.splice(0, this.section_fields.length);
  }

  static isWorkflowWorkflowFormFieldSubmissionDataFieldError(
    data: IWorkflowFormSectionFieldErrorData
  ): boolean {
    if (data.title && Array.isArray(data.title)) {
      return true;
    } else if (data.order && Array.isArray(data.order)) {
      return true;
    } else if (data.section_fields && Array.isArray(data.section_fields)) {
      return true;
    }
    return false;
  }
}

export class WorkflowFormSectionFieldFieldError
  implements IWorkflowFormSectionFieldFieldError {
  readonly field: IWorkflowFormFieldFieldError[] = [];
  readonly width: string[] = [];
  readonly order: string[] = [];

  constructor(data?: IWorkflowFormSectionFieldFieldErrorData) {
    if (data) {
      if (data.order) {
        this.order = data.order;
      }
      if (data.width) {
        this.width = data.width;
      }
      if (data.field) {
        this.field = data.field.map(
          field => new WorkflowFormFieldFieldErrorData(field)
        );
      }
    }
  }

  clearErrors(): void {
    this.width.splice(0, this.width.length);
    this.order.splice(0, this.order.length);
    this.field.splice(0, this.field.length);
  }

  static isWorkflowFormFieldSubmissionDataFieldError(
    data: IWorkflowFormSectionFieldFieldErrorData
  ): boolean {
    if (data.width && Array.isArray(data.width)) {
      return true;
    } else if (data.order && Array.isArray(data.order)) {
      return true;
    } else if (data.field && Array.isArray(data.field)) {
      return true;
    }
    return false;
  }
}

export class WorkflowFormFieldFieldErrorData
  implements IWorkflowFormFieldFieldError {
  readonly label: string[] = [];
  readonly meta_label: string[] = [];
  readonly hint: string[] = [];
  readonly type: string[] = [];
  readonly required: string[] = [];
  readonly create_todo: string[] = [];
  readonly todo_department: IDepartmentFieldError[] = [];
  readonly options: IWorkflowFormFieldOptionFieldError[] = [];
  readonly style: IWorkflowFormFieldStyleFieldError[] = [];
  readonly order: string[] = [];

  constructor(data?: IWorkflowFormFieldFieldErrorData) {
    if (data) {
      if (data.label) {
        this.label = data.label;
      }
      if (data.meta_label) {
        this.meta_label = data.meta_label;
      }
      if (data.hint) {
        this.hint = data.hint;
      }
      if (data.type) {
        this.type = data.type;
      }
      if (data.required) {
        this.required = data.required;
      }
      if (data.create_todo) {
        this.create_todo = data.create_todo;
      }
      if (data.todo_department) {
        this.todo_department = data.todo_department.map(
          department => new DepartmentFieldError(department)
        );
      }
    }
  }

  clearErrors(): void {
    this.label.splice(0, this.label.length);
    this.order.splice(0, this.order.length);
    this.meta_label.splice(0, this.meta_label.length);
    this.hint.splice(0, this.hint.length);
    this.type.splice(0, this.type.length);
    this.required.splice(0, this.required.length);
    this.create_todo.splice(0, this.create_todo.length);
    this.todo_department.splice(0, this.todo_department.length);
    this.options.splice(0, this.options.length);
    this.style.splice(0, this.style.length);
    this.order.splice(0, this.order.length);
  }

  static isWorkflowFormFieldSubmissionDataFieldError(
    data: IWorkflowFormFieldFieldErrorData
  ): boolean {
    if (data.label && Array.isArray(data.label)) {
      return true;
    } else if (data.order && Array.isArray(data.order)) {
      return true;
    } else if (data.meta_label && Array.isArray(data.meta_label)) {
      return true;
    } else if (data.hint && Array.isArray(data.hint)) {
      return true;
    } else if (data.required && Array.isArray(data.required)) {
      return true;
    } else if (data.create_todo && Array.isArray(data.create_todo)) {
      return true;
    } else if (data.todo_department && Array.isArray(data.todo_department)) {
      return true;
    } else if (data.options && Array.isArray(data.options)) {
      return true;
    } else if (data.style && Array.isArray(data.style)) {
      return true;
    }
    return false;
  }
}

// Access Condition Node Classes
export class AccessPredicateNodeOperatorInfo
  extends AbstractPredicateNodeOperatorInfo<AccessPredicateNodeBinaryOperators>
  implements IAccessPredicateNodeOperatorInfo {
  constructor(data: IAccessPredicateNodeOperatorInfo) {
    super(data);
  }

  static Equals = new AccessPredicateNodeOperatorInfo({
    display_name: 'Equals',
    value: AccessPredicateNodeBinaryOperators.Equals,
    icon: 'mdi-equal'
  });

  static NotEqual = new AccessPredicateNodeOperatorInfo({
    display_name: 'Not equals',
    value: AccessPredicateNodeBinaryOperators.NotEqual,
    icon: 'mdi-not-equal'
  });

  static GreaterThan = new AccessPredicateNodeOperatorInfo({
    display_name: 'Greater than',
    value: AccessPredicateNodeBinaryOperators.GreaterThan,
    icon: 'mdi-greater-than'
  });

  static GreaterThanEqual = new AccessPredicateNodeOperatorInfo({
    display_name: 'Greater than or equals',
    value: AccessPredicateNodeBinaryOperators.GreaterThanEqual,
    icon: 'mdi-greater-than-or-equal'
  });

  static LessThan = new AccessPredicateNodeOperatorInfo({
    display_name: 'Less than',
    value: AccessPredicateNodeBinaryOperators.LessThan,
    icon: 'mdi-less-than'
  });

  static LessThanEqual = new AccessPredicateNodeOperatorInfo({
    display_name: 'Less than or equals',
    value: AccessPredicateNodeBinaryOperators.LessThanEqual,
    icon: 'mdi-less-than-or-equal'
  });

  static StartBracket = new AccessPredicateNodeOperatorInfo({
    display_name: 'Start Bracket',
    value: AccessPredicateNodeBinaryOperators.StartBracket,
    icon: ')'
  });

  static CloseBracket = new AccessPredicateNodeOperatorInfo({
    display_name: 'Close Bracket',
    value: AccessPredicateNodeBinaryOperators.CloseBracket,
    icon: '('
  });

  static getAccessPredicateNodeOperator(
    value: AccessPredicateNodeBinaryOperators
  ): IAccessPredicateNodeOperatorInfo | null {
    let stateInfo = null;
    switch (value) {
      case AccessPredicateNodeBinaryOperators.Equals:
        stateInfo = AccessPredicateNodeOperatorInfo.Equals;
        break;

      case AccessPredicateNodeBinaryOperators.NotEqual:
        stateInfo = AccessPredicateNodeOperatorInfo.NotEqual;
        break;

      case AccessPredicateNodeBinaryOperators.GreaterThan:
        stateInfo = AccessPredicateNodeOperatorInfo.GreaterThan;
        break;

      case AccessPredicateNodeBinaryOperators.GreaterThanEqual:
        stateInfo = AccessPredicateNodeOperatorInfo.GreaterThanEqual;
        break;

      case AccessPredicateNodeBinaryOperators.LessThan:
        stateInfo = AccessPredicateNodeOperatorInfo.LessThan;
        break;

      case AccessPredicateNodeBinaryOperators.LessThanEqual:
        stateInfo = AccessPredicateNodeOperatorInfo.LessThanEqual;
        break;
    }

    return stateInfo;
  }

  static getAccessPredicateNodeOperatorInfoList(): IAccessPredicateNodeOperatorInfo[] {
    return [
      AccessPredicateNodeOperatorInfo.Equals,
      AccessPredicateNodeOperatorInfo.NotEqual,
      AccessPredicateNodeOperatorInfo.GreaterThan,
      AccessPredicateNodeOperatorInfo.GreaterThanEqual,
      AccessPredicateNodeOperatorInfo.LessThan,
      AccessPredicateNodeOperatorInfo.LessThanEqual
    ];
  }
}

export class AccessPredicateNodeOperandTypeInfo
  extends AbstractPredicateNodeOperatorInfo<AccessPredicateNodeOperandSubtype>
  implements IAccessPredicateNodeOperandTypeInfo {
  constructor(data: IAccessPredicateNodeOperandTypeInfo) {
    super(data);
  }

  static Constant = new AccessPredicateNodeOperandTypeInfo({
    display_name: 'Constant',
    value: AccessPredicateNodeOperandSubtype.Constant,
    icon: 'mdi-numeric'
  });

  static Variable = new AccessPredicateNodeOperandTypeInfo({
    display_name: 'Property',
    value: AccessPredicateNodeOperandSubtype.Variable,
    icon: 'mdi-property'
  });

  static getAccessPredicateNodeOperandType(
    value: AccessPredicateNodeOperandSubtype
  ): IAccessPredicateNodeOperandTypeInfo {
    let stateInfo: IAccessPredicateNodeOperandTypeInfo;
    switch (value) {
      case AccessPredicateNodeOperandSubtype.Constant:
        stateInfo = AccessPredicateNodeOperandTypeInfo.Constant;
        break;

      case AccessPredicateNodeOperandSubtype.Variable:
        stateInfo = AccessPredicateNodeOperandTypeInfo.Variable;
        break;
    }
    return stateInfo;
  }

  static getAccessPredicateNodeOperandTypeInfoList(): IAccessPredicateNodeOperandTypeInfo[] {
    return [
      AccessPredicateNodeOperandTypeInfo.Constant,
      AccessPredicateNodeOperandTypeInfo.Variable
    ];
  }
}

export class AccessPredicateNode
  extends AbstractPredicateNode<
    AccessPredicateNodeBinaryOperators | AccessPredicateNodeOperandSubtype
  >
  implements IAccessPredicateNode {
  readonly id?: number;
  readonly variable_name: string;

  constructor(data: IAccessPredicateNodeData) {
    super(data);

    if (data.id) {
      this.id = data.id;
    }

    this.variable_name = data.variable_name ?? '';
  }

  toOperandString(): string {
    const hashOperandValue: Record<
      AccessPredicateNodeOperandSubtype,
      string
    > = {
      [AccessPredicateNodeOperandSubtype.Constant]: `${this.constant}`,
      [AccessPredicateNodeOperandSubtype.Variable]: `${this.variable_name}`
    };

    return (
      hashOperandValue[this.subtype as AccessPredicateNodeOperandSubtype] ||
      `Unknown(${this.subtype})`
    );
  }

  toString() {
    if (this.isOperator()) {
      return this.toOperatorString();
    } else {
      return this.toOperandString();
    }
  }
}

export class AccessConditionTarget extends AbstractModel
  implements IAccessConditionTarget {
  public readonly id?: number;
  public readonly target_type: AccessTargetType;
  public readonly target_group: Group | number | null = null;
  public readonly target_user: IUser | number | null = null;

  constructor(data: IAccessConditionTargetData) {
    super();

    this.validate(data, ['target_type']);

    if (data.id) {
      this.id = data.id;
    }

    this.target_type = data.target_type!;

    if (data.target_group) {
      this.target_group =
        typeof data.target_group === 'number'
          ? data.target_group
          : new Group(data.target_group);
    }

    if (data.target_user) {
      this.target_user =
        typeof data.target_user === 'number'
          ? data.target_user
          : new User(data.target_user);
    }
  }

  toString(): string {
    if (this.target_type === AccessTargetType.User && this.target_user) {
      return typeof this.target_user === 'number'
        ? `User #${this.target_user}`
        : this.target_user.toString();
    } else if (
      this.target_type === AccessTargetType.Group &&
      this.target_group
    ) {
      return typeof this.target_group === 'number'
        ? `Group #${this.target_group}`
        : this.target_group.toString();
    }
    return 'None';
  }
}

export class WorkflowFormTemplateRevisionAccessCondition extends AbstractModel
  implements IWorkflowFormTemplateRevisionAccessCondition {
  public readonly id?: number;
  public readonly name: string;
  public readonly targets: IAccessConditionTarget[] = [];
  public readonly nodes: IAccessPredicateNode[] = [];

  constructor(data: IWorkflowFormTemplateRevisionAccessConditionData) {
    super();

    this.validate(data, ['name']);

    if (data.id) {
      this.id = data.id;
    }

    this.name = data.name!;

    if (data.targets) {
      this.targets = data.targets.map(
        target => new AccessConditionTarget(target)
      );
    }

    if (data.nodes) {
      this.nodes = data.nodes.map(node => new AccessPredicateNode(node));
    }
  }

  toString() {
    return `${this.name} → ${this.getTarget()}`;
  }

  getTarget(): string {
    if (this.targets.length === 0) {
      return 'None';
    }

    const targetStrings = this.targets.map(target => target.toString());
    return targetStrings.join(', ');
  }

  toExpressionTree(): ExpressionPredicateNodeTree<IAccessPredicateNode> {
    return convertPredicateNodeListToLogicalExpressionTree(this.nodes);
  }
}
